import { Button, CircularProgress, Dialog, DialogBody, DialogContent, IconButton } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Announcement, AnnouncementContent, BulletItem } from './announcements';

type Props = {
  announcement: Announcement;
};

export default function AnnouncementModal({ announcement }: Props) {
  const { name } = announcement;

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (announcement.expiration && DateTime.now() > announcement.expiration) {
      return;
    }
    if (localStorage.getItem(name)) {
      setShowModal(false);
    } else {
      // 1 second timeout so modal does not appear immediately after accepting the disclaimer
      setTimeout(() => {
        setShowModal(true);
      }, 1_000);

      // add a value 'announcement' to each modal in case we want to see all announcements viewed in user's localStorage
      localStorage.setItem(name, 'announcement');
    }
  }, [name, announcement.expiration]);

  useEffect(() => {
    if (showModal) {
      capturePosthogEvent(POSTHOG_EVENTS.announcementViewed, {
        announcement_name: name,
      });
    }
  }, [showModal, name]);

  if (!showModal || (announcement.expiration && DateTime.now() > announcement.expiration)) {
    return null;
  }

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent
        title="Announcement"
        titleProps={{ className: 'sr-only' }}
        headerProps={{ className: 'absolute top-0 right-0 p-0 pt-3 pr-3' }}
        preventDismiss
        className={classNames('z-[110] w-auto max-w-6xl max-h-[90vh] rounded-sm', announcement.modalClassName)}
        overlayClassName="z-[110]"
        data-testid="announcement-dialog"
      >
        <DialogBody className="p-8">
          <ModalContent announcement={announcement} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

type ModalContentProps = {
  announcement: Announcement;
};

const ModalContent = ({ announcement }: ModalContentProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { name, content, buttons } = announcement;

  const navigate = useNavigate();

  useEffect(() => {
    capturePosthogEvent(POSTHOG_EVENTS.announcementPageViewed, {
      announcement_name: name,
      page_title: content[currentPage].title,
      page_number: currentPage,
    });
  }, [name, currentPage, content]);

  return (
    <div className="grid h-full w-full rounded-sm">
      <div className="flex h-full flex-col justify-between">
        <div className="flex flex-1 flex-col space-y-4 overflow-y-auto">
          <div className="text-xl">{content[currentPage].title}</div>
          <div>{content[currentPage].message}</div>
          <ContentRenderer item={content[currentPage]} />
        </div>
        <div className="pt-4">
          <div className="flex flex-row justify-center space-x-2">
            {buttons.map((button) => {
              if (button.linkType === 'external') {
                return (
                  <a
                    href={button.link}
                    target="_blank"
                    data-testid={`announcement-cta-external-${button.name}`}
                    onClick={() => {
                      capturePosthogEvent(POSTHOG_EVENTS.announcementCtaClicked, {
                        announcement_name: name,
                        button_name: button.name,
                        link: button.link,
                        link_type: button.linkType,
                      });
                    }}
                  >
                    <Button variant="secondary">{button.name}</Button>
                  </a>
                );
              } else if (button.linkType === 'internal') {
                return (
                  <Button
                    variant="secondary"
                    data-testid={`announcement-cta-internal-${button.name}`}
                    onClick={() => {
                      navigate({ to: button.link });
                      capturePosthogEvent(POSTHOG_EVENTS.announcementCtaClicked, {
                        announcement_name: name,
                        button_name: button.name,
                        link: button.link,
                        link_type: button.linkType,
                      });
                    }}
                  >
                    {button.name}
                  </Button>
                );
              } else {
                return null;
              }
            })}
          </div>
        </div>
        {content.length > 1 && (
          <div className="flex items-center justify-center space-x-5">
            <div
              className={classNames('flex items-center justify-center', {
                invisible: currentPage === 0,
              })}
            >
              <IconButton
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                icon="chevronLeft"
                iconSize={10}
                variant="secondary"
                aria-label="Previous announcement"
                data-testid="announcement-previous"
              />
            </div>

            <NavigationDots totalPages={content.length} currentPage={currentPage} setCurrentPage={setCurrentPage} />

            <div
              className={classNames('flex items-center justify-center', {
                invisible: currentPage === content.length - 1,
              })}
            >
              <IconButton
                icon="chevronRight"
                iconSize={10}
                variant="secondary"
                aria-label="Next announcement"
                data-testid="announcement-next"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, content.length - 1))}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type NavigationDotsProps = {
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
};

const NavigationDots = ({ totalPages, currentPage, setCurrentPage }: NavigationDotsProps) => (
  <div className="flex h-full items-center justify-center space-x-2 ">
    {[...Array(totalPages)].map((_, index) => (
      <button
        // biome-ignore lint/suspicious/noArrayIndexKey: legacy
        key={index}
        type="button"
        aria-label={`Go to announcement ${index + 1}`}
        aria-current={index === currentPage ? 'true' : undefined}
        data-testid={`announcement-page-dot-${index}`}
        className={classNames(
          'cursor-pointer rounded-full border-0 p-0 outline-hidden transition-all duration-200 focus-ring',
          {
            'h-4 w-4 bg-brand': index === currentPage,
            'h-2 w-2 bg-slate-300': index !== currentPage,
          },
        )}
        onClick={() => {
          setCurrentPage(index);
        }}
      />
    ))}
  </div>
);

type ContentRendererProps = {
  item: AnnouncementContent;
};

const ContentRenderer = ({ item }: ContentRendererProps) => {
  const [isLoomLoading, setIsLoomLoading] = useState(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setIsLoomLoading(true);
  }, [item]);

  switch (item.type) {
    case 'jsx':
      return item.content;
    case 'loom':
      return (
        <div className="h-full w-full">
          {isLoomLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <CircularProgress indeterminate />
            </div>
          )}
          <iframe
            src={item.content}
            title={item.title ?? 'Announcement video'}
            className={`h-full w-full ${isLoomLoading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setIsLoomLoading(false)}
          />
        </div>
      );
    case 'image':
      return (
        <div className="h-full w-auto">
          <img src={item.content} alt={item.title} className="h-full w-full object-contain" />
        </div>
      );
    case 'bulletedList':
      return (
        <div className="px-4 text-left">
          {(item.content as BulletItem[]).map((bulletItem, sectionIndex) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={`bulleted-section-${sectionIndex}`}
              className="mb-4"
            >
              <h3 className="mb-2 text-base font-bold">{bulletItem.title}</h3>
              <ul className="list-inside list-disc space-y-1">
                {bulletItem.bullets.map((bullet, bulletIndex) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                    key={`bulleted-section-${sectionIndex}-bullet-${bulletIndex}`}
                  >
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
};
